import { useEffect, useState } from 'react';
import usePageMeta from '../utils/usePageMeta';
import pagesSeo from '../data/pagesSeo.json';
import Header from '../components/core/Header';
import Footer from '../components/core/Footer';
import styles from './PublicationsPage.module.css';
import typography from '../theme/typography';
import colors from '../theme/colors';
import publicationsFallback from '../data/publications';
import {
  fetchPublicationsPageData,
} from '../contentful/publicationsApi';
import { isContentfulConfigured } from '../contentful/client';

const isHttpUrl = (value) => /^https?:\/\//i.test(String(value || '').trim());

const PublicationTitle = ({ title, url }) => {
  const titleStyle = { fontFamily: "'Jost', sans-serif", fontSize: '20px', fontStyle: 'normal', fontWeight: 400, lineHeight: '28px', color: colors.dark };
  if (isHttpUrl(url)) {
    return (
      <a
        href={url.trim()}
        className={`${styles.itemTitle} ${styles.itemTitleLink}`}
        style={titleStyle}
        target="_blank"
        rel="noopener noreferrer"
      >
        {title}
      </a>
    );
  }
  return (
    <p
      className={styles.itemTitle}
      style={titleStyle}
    >
      {title}
    </p>
  );
};

const PublicationsPage = () => {
  usePageMeta(pagesSeo.publications);
  const [yearData, setYearData] = useState(() =>
    isContentfulConfigured() ? null : publicationsFallback
  );
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!isContentfulConfigured()) {
      return;
    }

    let cancelled = false;
    setLoadError(null);

    fetchPublicationsPageData()
      .then((data) => {
        if (!cancelled) {
          setYearData(data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(
            err?.message || 'Could not load publications from Contentful.'
          );
          setYearData([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const showLoading = yearData === null;
  const rows = yearData ?? [];

  const totalPublications = rows.reduce(
    (sum, y) => sum + y.publications.length,
    0
  );
  const totalTheses = rows.reduce((sum, y) => sum + y.theses.length, 0);

  return (
    <div>
      <Header />

      <section className={styles.pageSection}>
        <div className={styles.container}>

          <div className={styles.pageHeader}>
            <h1
              className={`${styles.pageTitle} page-primary-title`}
              style={{ ...typography.heading.large, color: colors.dark }}
            >
              PUBLICATIONS
            </h1>
            <div
              className={styles.totalCount}
              style={{ ...typography.body.xlarge.regular, color: colors.dark }}
            >
              {showLoading ? (
                'Loading…'
              ) : (
                <>
                  <span style={{ color: colors.orange[700] }}>{totalPublications}</span>
                  {' Publications, '}
                  <span style={{ color: colors.orange[700] }}>{totalTheses}</span>
                  {' Thesis'}
                </>
              )}
            </div>
          </div>

          {loadError && (
            <p
              className={styles.loadError}
              style={{ ...typography.body.medium.regular, color: colors.gray[800] }}
              role="alert"
            >
              {loadError}
            </p>
          )}

          <div className={styles.pageDivider} />

          {showLoading ? (
            <p
              style={{ ...typography.body.large.regular, color: colors.gray[800] }}
            >
              Loading publications…
            </p>
          ) : (
            rows.map((yearDataItem) => {
              const pubCount = yearDataItem.publications.length;
              const thesisCount = yearDataItem.theses.length;

              return (
                <div key={yearDataItem.year} className={styles.yearSection}>

                  <div className={styles.yearHeader}>
                    <h2
                      className={styles.yearTitle}
                      style={{ fontFamily: "'Jost', sans-serif", fontSize: '30px', fontStyle: 'normal', fontWeight: 500, lineHeight: '38px', color: colors.dark }}
                    >
                      {yearDataItem.year}
                    </h2>
                    <div
                      className={styles.yearCount}
                      style={{ ...typography.body.large.regular, color: colors.dark }}
                    >
                      <span style={{ color: colors.orange[700] }}>{pubCount}</span>
                      {` Publication${pubCount !== 1 ? 's' : ''}, `}
                      <span style={{ color: colors.orange[700] }}>{thesisCount}</span>
                      {` Thes${thesisCount !== 1 ? 'es' : 'is'}`}
                    </div>
                  </div>

                  <div className={styles.yearDivider} />

                  {yearDataItem.publications.length > 0 && (
                    <div className={styles.subsection}>
                      <p
                        className={styles.subsectionLabel}
                        style={{ ...typography.eyebrow.small, color: colors.gray[700] }}
                      >
                        Publications
                      </p>
                      <div className={styles.itemGrid}>
                        {yearDataItem.publications.map((pub, index) => (
                          <div
                            key={pub.id ?? `pub-${yearDataItem.year}-${index}`}
                            className={styles.publicationItem}
                          >
                            <PublicationTitle title={pub.title} url={pub.url} />
                            <p
                              className={styles.itemAuthors}
                              style={{ ...typography.body.medium.regular, color: colors.orange[700] }}
                            >
                              {pub.authors}
                            </p>
                            <p
                              className={styles.itemMeta}
                              style={{ ...typography.body.medium.regular, color: colors.gray[800] }}
                            >
                              {pub.venue}
                            </p>
                            <p
                              className={styles.itemDate}
                              style={{ ...typography.body.medium.regular, color: colors.gray[800] }}
                            >
                              {pub.date}
                              {pub.date ? '.' : ''}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {yearDataItem.theses.length > 0 && (
                    <div className={styles.subsection}>
                      <p
                        className={styles.subsectionLabel}
                        style={{ ...typography.eyebrow.small, color: colors.gray[700] }}
                      >
                        Theses
                      </p>
                      <div className={styles.itemGrid}>
                        {yearDataItem.theses.map((thesis, index) => (
                          <div
                            key={thesis.id ?? `thesis-${yearDataItem.year}-${index}`}
                            className={styles.publicationItem}
                          >
                            <PublicationTitle title={thesis.title} url={thesis.url || '#'} />
                            <p
                              className={styles.itemAuthors}
                              style={{ ...typography.body.medium.regular, color: colors.orange[700] }}
                            >
                              {thesis.author}
                            </p>
                            <p
                              className={styles.itemMeta}
                              style={{ ...typography.body.medium.regular, color: colors.gray[800] }}
                            >
                              {thesis.degree}
                            </p>
                            <p
                              className={styles.itemDate}
                              style={{ ...typography.body.medium.regular, color: colors.gray[800] }}
                            >
                              {thesis.date}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              );
            })
          )}

        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PublicationsPage;
